"use client";

import { useRef } from "react";
import { Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { attachmentProblem, formatFileSize } from "@/lib/inbox/attachments";

/** The Attach button: adds files to the list, refusing what Gmail won't send. */
export function AttachButton({
  files,
  onChange,
  disabled,
  className,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  className?: string;
}) {
  const input = useRef<HTMLInputElement>(null);

  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const next = [...files, ...Array.from(e.target.files ?? [])];
          // The same file can be picked again after removing it.
          e.target.value = "";
          const problem = attachmentProblem(next);
          if (problem) {
            toast.error(problem);
            return;
          }
          onChange(next);
        }}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => input.current?.click()}
        className={cn(
          "flex items-center gap-1.5 rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-60",
          className,
        )}
      >
        <Paperclip className="h-3.5 w-3.5" /> Attach
      </button>
    </>
  );
}

/** The files attached so far, each removable. */
export function AttachmentList<T extends { name: string; size: number }>({
  files,
  onChange,
  disabled,
  className,
}: {
  files: T[];
  onChange: (files: T[]) => void;
  disabled?: boolean;
  className?: string;
}) {
  if (files.length === 0) return null;

  return (
    <ul className={cn("flex flex-wrap gap-1.5", className)}>
      {files.map((file, index) => (
        <li
          key={`${file.name}-${file.size}-${index}`}
          className="flex max-w-[260px] items-center gap-1.5 rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
        >
          <Paperclip className="h-3 w-3 shrink-0 text-gray-400" />
          <span className="truncate" title={file.name}>
            {file.name}
          </span>
          <span className="shrink-0 text-gray-400">
            {formatFileSize(file.size)}
          </span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange(files.filter((_, i) => i !== index))}
            className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            title={`Remove ${file.name}`}
          >
            <X className="h-3 w-3" />
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Sends a reply or new mail with attachments through /api/inbox/send; `mail`
 * is what the sendReply / sendNewMail action would take.
 */
export async function sendWithAttachments(
  kind: "reply" | "new",
  mail: Record<string, unknown>,
  files: File[],
): Promise<{ success: boolean; error?: string; data?: string }> {
  const form = new FormData();
  form.append("mail", JSON.stringify({ ...mail, kind }));
  for (const file of files) form.append("attachments", file, file.name);

  let response: Response;
  try {
    response = await fetch("/api/inbox/send", { method: "POST", body: form });
  } catch {
    return { success: false, error: "Could not reach the server" };
  }
  try {
    return await response.json();
  } catch {
    // Not the route's own answer: a proxy refusing the size, or a gateway
    // giving up while the route may still have sent the mail.
    return {
      success: false,
      error:
        response.status === 413
          ? "Attachments are too large to upload"
          : `The server did not answer (HTTP ${response.status}). It may have been sent - check before sending again.`,
    };
  }
}
